import { useReduxState } from 'hooks';
import { getResizedImage } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { breakpoints } from 'styles';
import { anchorText } from 'lib/common';
import { newlinesToLineBreak, renderTextWithEmoji } from 'lib/helpers';
import {
  Alert,
  ApiDownloadButton,
  Badge,
  ContentLabel,
  ContentValue,
  FileLink,
  FlexContainer,
  Link,
  LoadingIndicator,
  RichTextValue,
  User
} from 'components/common';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';
import { CALENDAR_EVENT_IMAGE_ASPECT_RATIO } from './CalendarEventImage';
import {
  CalendarEventAddToMyCalendar,
  CalendarEventDateString,
  CalendarEventParticipantsEditCustomAttributesSelf,
  CalendarEventRenderInfoAchievement,
  CalendarEventStatus
} from '.';

import type { CalendarEventExtended } from 'shared-types';
import type { GetCalendarEventAdditionalRenderingInfoRes } from 'shared-types/responses';

interface Props {
  event: CalendarEventExtended;
  eventAchievements: GetCalendarEventAdditionalRenderingInfoRes['achievements'];
  eventAttachments: Record<'fileUrl' | 'filename' | 'extension', string>[];
  eventCompetencies: GetCalendarEventAdditionalRenderingInfoRes['competencies'];
  hasPrivileges?: boolean;
  infoLoading: boolean;
  setEvent?: React.Dispatch<React.SetStateAction<CalendarEventExtended>>;
}

const CalendarEventRenderInfo = ({
  event,
  eventAchievements,
  eventAttachments,
  eventCompetencies,
  hasPrivileges = false,
  infoLoading,
  setEvent
}: Props) => {
  const { language, messages, serverConfig, theme, user } = useReduxState();

  const renderMessage = (text: string) => {
    const anchoredText = newlinesToLineBreak(
      anchorText({ text, sanitize: true })
    );
    return renderTextWithEmoji(anchoredText, 20).map((p: string, i: number) => (
      <span key={i} dangerouslySetInnerHTML={{ __html: p }} />
    ));
  };

  const availableSpots =
    event.participationLimit - event.numberOfParticipantsAdded;

  return infoLoading ? (
    <LoadingIndicator />
  ) : (
    <div className="container">
      {event.imageUrl ? (
        <div
          className="image"
          style={{
            backgroundImage: `url(${getResizedImage({
              size: 1920,
              url: event.imageUrl
            })})`
          }}
        />
      ) : null}

      <div className="event-body">
        <div className="title">{event.title}</div>

        {event.description ? (
          <div className="description">
            <RichTextValue html={event.description} />
          </div>
        ) : null}

        <div className="details">
          <DisplayAttributeInProductPage
            customAttributes={event.customAttributes}
            position="at start"
            productType="calendarEvent"
          />

          {!serverConfig.productHideOwner && (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.commonOwners}
              spacing={20}
            >
              <ContentValue>
                <div className="owners">
                  {event.owners.map((owner) => (
                    <User
                      key={owner.id}
                      size="small"
                      showEmail={false}
                      showEmailTooltip={false}
                      titleStyle={{ fontSize: '1.4rem' }}
                      user={owner}
                    />
                  ))}
                </div>
              </ContentValue>
            </ContentLabel>
          )}

          <ContentLabel
            horizontal={true}
            horizontalSpacing={50}
            label={messages.commonStatus}
            spacing={20}
          >
            <ContentValue
              style={{ color: theme.textColorLighter, fontWeight: 600 }}
            >
              <CalendarEventStatus event={event} />
            </ContentValue>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            horizontalSpacing={50}
            label={messages.commonTime}
            spacing={20}
          >
            <ContentValue
              icon={['far', 'calendar-alt']}
              style={{ fontWeight: 600 }}
            >
              <CalendarEventDateString
                startDate={event.startDate}
                endDate={event.endDate}
              />
            </ContentValue>

            {hasPrivileges || event.userStatus === 'queued' ? (
              <div className="add-to-calendar">
                <CalendarEventAddToMyCalendar event={event} />
              </div>
            ) : null}
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            horizontalSpacing={50}
            label={messages.eventCalendarJoinLastDate}
            spacing={20}
          >
            <ContentValue
              icon={['far', 'calendar-alt']}
              style={{ fontWeight: 600 }}
            >
              <CalendarEventDateString
                startDate={event.lastJoinDate || event.startDate}
              />
            </ContentValue>
          </ContentLabel>

          {event.link ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.commonLink}
              spacing={20}
            >
              <ContentValue
                icon="link"
                style={{ fontWeight: 600, wordBreak: 'break-all' }}
              >
                <Link
                  external={true}
                  href={`${
                    event.link.startsWith('http://') ||
                    event.link.startsWith('https://')
                      ? ''
                      : 'https://'
                  }${event.link}`}
                  openInNewTab={true}
                  openInNewTabIcon={false}
                  text={event.linkText ? event.linkText : event.link}
                />
              </ContentValue>
            </ContentLabel>
          ) : null}

          {event.location ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.commonLocation}
              spacing={20}
            >
              <ContentValue
                icon="map-marker-alt"
                style={{ fontWeight: 600, wordBreak: 'break-all' }}
              >
                <div className="location">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: newlinesToLineBreak(
                        anchorText({ text: event.location, sanitize: true })
                      )
                    }}
                  />
                </div>
              </ContentValue>
            </ContentLabel>
          ) : null}

          <ContentLabel
            horizontal={true}
            horizontalSpacing={50}
            label={messages.commonParticipants}
            spacing={20}
          >
            <ContentValue icon="user" style={{ fontWeight: 600 }}>
              {`${event.numberOfParticipantsAdded}${
                event.participationLimit > 0
                  ? ` / ${event.participationLimit}`
                  : ''
              }`}

              {availableSpots > 0 || event.numberOfParticipantsQueued > 0 ? (
                <span style={{ fontWeight: 400, marginLeft: 10 }}>
                  {availableSpots > 0
                    ? getMessageWithPlaceholders({
                        message: messages.eventCalendarAvailablePlaces,
                        placeholders: {
                          numberOfPlaces: availableSpots
                        }
                      })
                    : event.numberOfParticipantsQueued > 0
                    ? `(${getMessageWithPlaceholders({
                        message: messages.eventCalendarUsersInQueue,
                        placeholders: {
                          numberOfUsers: event.numberOfParticipantsQueued
                        }
                      })})`
                    : ''}
                </span>
              ) : null}
            </ContentValue>
          </ContentLabel>

          {event.workloadIsEnabled ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.settingsCompletionWorkload}
              spacing={20}
            >
              <ContentValue style={{ fontWeight: 600 }}>
                {getMessageWithPlaceholders({
                  message: messages.commonNumberOfHours,
                  placeholders: {
                    numberOfHours: new Intl.NumberFormat(language).format(
                      event.workloadValue
                    )
                  }
                })}
              </ContentValue>
            </ContentLabel>
          ) : null}

          {event.categories.length ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.commonCategories}
              spacing={20}
            >
              <ContentValue>
                <div className="info-badge">
                  {event.categories.map((category) => (
                    <Badge key={category} text={category} size="large" />
                  ))}
                </div>
              </ContentValue>
            </ContentLabel>
          ) : null}

          {eventAchievements?.length ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.eventCalendarAchievementGranted}
              spacing={20}
            >
              <ContentValue>
                <div className="achievements">
                  {eventAchievements.map((achievement) => (
                    <CalendarEventRenderInfoAchievement
                      achievement={achievement}
                      key={achievement.id}
                    />
                  ))}
                </div>
              </ContentValue>
            </ContentLabel>
          ) : null}

          {eventCompetencies?.length ? (
            <ContentLabel
              horizontal={true}
              horizontalSpacing={50}
              label={messages.competenciesGranted}
              spacing={20}
            >
              <ContentValue>
                <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                  {eventCompetencies.map((competency) => (
                    <CompetenciesProductBadge
                      competency={competency}
                      key={`${competency.id}${
                        competency.level ? `-${competency.level.id}` : ''
                      }`}
                    />
                  ))}
                </FlexContainer>
              </ContentValue>
            </ContentLabel>
          ) : null}

          <DisplayAttributeInProductPage
            customAttributes={event.customAttributes}
            productType="calendarEvent"
          />
        </div>

        {event.userAttributes?.length && event?.userStatus === 'added' ? (
          <div className="custom-attributes">
            <div className="custom-attributes-header">
              <div className="custom-attributes-title">
                {messages.eventCalendarParticipationDetails}
              </div>
              <CalendarEventParticipantsEditCustomAttributesSelf
                event={event}
                setEvent={setEvent}
              />
            </div>

            <div className="custom-attributes-content">
              {event.userAttributes.map((attr) => {
                const title = attr.titleTranslations?.[language] || attr.title;
                const value = event.reqUserCustomAttributes?.[attr.id];

                return (
                  <ContentLabel
                    horizontal={true}
                    horizontalSpacing={50}
                    key={attr.id}
                    label={title}
                  >
                    <ContentValue style={{ fontWeight: 600 }}>
                      {attr.type === 'Boolean' && value !== undefined
                        ? value
                          ? messages.commonYes
                          : messages.commonNo
                        : Array.isArray(value) && value.length
                        ? value.join(', ')
                        : value || '-'}
                    </ContentValue>
                  </ContentLabel>
                );
              })}
            </div>
          </div>
        ) : null}

        {event.userParticipated && event.certificateAllowDownloading ? (
          <ContentLabel
            horizontal={true}
            label={messages.commonCertificate}
            style={{ marginTop: 30 }}
          >
            <ApiDownloadButton
              category="link-with-background"
              filename={`${event.title}.pdf`}
              icon={['far', 'file-pdf']}
              iconStyle={{
                color: '#F40F02',
                fontSize: '1.4rem',
                top: 0
              }}
              size="header"
              text={messages.completionsDownloadCertificate}
              url={
                `/admin/calendar-event-certificate` +
                `?eventId=${event.id}` +
                `&userId=${user.id}` +
                `&language=${language}`
              }
            />
          </ContentLabel>
        ) : null}

        {hasPrivileges && event.message ? (
          <div className="info-row">
            <div style={{ width: '100%' }}>
              <Alert
                iconVisible={false}
                text={
                  <ContentLabel label={messages.eventCalendarParticipantInfo}>
                    <ContentValue style={{ display: 'block' }}>
                      {renderMessage(event.message)}
                    </ContentValue>
                  </ContentLabel>
                }
                type="info"
                size="large"
              />
            </div>
          </div>
        ) : null}

        {hasPrivileges && eventAttachments.length ? (
          <ContentLabel label={messages.commonFiles} style={{ marginTop: 30 }}>
            <ContentValue>
              <div className="info-badge">
                {eventAttachments.map((attachment) => (
                  <div className="attachment" key={attachment.fileUrl}>
                    <FileLink
                      name={attachment.filename}
                      showDownloadButton={true}
                      styleAsButton={true}
                      url={attachment.fileUrl}
                    />
                  </div>
                ))}
              </div>
            </ContentValue>
          </ContentLabel>
        ) : null}

        {event.isFull && !event.userStatus ? (
          <Alert
            style={{ marginTop: 30 }}
            {...(!event.queueEnabled || event.isQueueFull
              ? { text: messages.eventCalendarIsFullInfo, type: 'error-light' }
              : {
                  text: messages.eventCalendarIsQueueOpen,
                  type: 'warning-light'
                })}
          />
        ) : null}
      </div>

      <style jsx>{`
        .container {
          padding: 15px;
        }
        .image {
          aspect-ratio: ${CALENDAR_EVENT_IMAGE_ASPECT_RATIO};
          background-position: center;
          background-size: cover;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.8rem;
          font-weight: 700;
          gap: 10px 30px;
        }
        .owners {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1.5;
          margin-top: 10px;
        }
        .details {
          margin-top: 30px;
        }
        .info-row {
          display: flex;
          flex-wrap: wrap;
          margin-top: 30px;
        }
        .info-row :global(.emoji-mart-emoji) {
          vertical-align: text-bottom;
        }
        .location :global(a) {
          color: ${theme.linkColor};
        }
        .add-to-calendar {
          margin-left: 22px;
          margin-top: 5px;
        }
        .add-to-calendar :global(.toggle:hover),
        .add-to-calendar :global(.toggle:focus) {
          text-decoration: underline;
        }
        .info-badge {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .achievements {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .attachment {
          align-items: flex-end;
          display: flex;
        }
        .custom-attributes {
          margin-top: 40px;
        }
        .custom-attributes-header {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .custom-attributes-title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .custom-attributes-content {
          margin-top: 20px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .container {
            padding: 20px;
          }
          .title {
            font-size: 2rem;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .container {
            padding: 25px;
          }
        }
      `}</style>
    </div>
  );
};

export default CalendarEventRenderInfo;
